import { useState } from "react";
import { Alert, Button, Paper, Stack, Typography } from "@mui/material";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { Promisable } from "type-fest";

import {
  openedWhereRequired,
  progressOf,
  withUnansweredSentAsNotAnswered,
  type Assessment,
  type AssessmentProgress,
} from "@thalamos/haven-common";

import guestApi from "../../api/guest/api.js";
import { AssessmentFacts } from "../../components/AssessmentFacts/AssessmentFacts.js";
import {
  SubmitAssessmentDialog,
  type ItemsNotAnswered,
  type Submission,
  type SubmitOutcome,
} from "../../components/SubmitAssessmentDialog/SubmitAssessmentDialog.js";
import { guestRoute } from "../../routing/guestRoute.js";
import {
  useAssessment,
  type Opening,
} from "../Assessment/AssessmentContext.js";
import { AssessmentDomainsTable } from "../Assessment/AssessmentDomainsTable.js";
import { submissionRequestOf } from "../Assessment/submissionRequestOf.js";
import {
  useGuestAssessmentDetails,
  type GuestAssessmentDetails,
} from "./GuestAssessmentLayout.js";

// The end of a UK calendar day, so it is shown as a UK date wherever the
// browser is.
const UK_CALENDAR_DATE = new Intl.DateTimeFormat("en-GB", {
  dateStyle: "long",
  timeZone: "Europe/London",
});
const CALENDAR_DATE_TIME = new Intl.DateTimeFormat("en-GB", {
  dateStyle: "long",
  timeStyle: "short",
});

/** What the thank-you page is told by the overview that sent the assessment. */
export type SentState = { askerName: string };

/**
 * The overview a guest lands on: the asker's message, every domain with its
 * progress, the assessment's key facts, and sending it to whoever asked.
 */
export const GuestAssessmentOverview = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { assessment, opening, flushNow } = useAssessment();
  const details = useGuestAssessmentDetails();

  if (assessment === undefined) {
    return null;
  }

  const send = async ({ assessedOn }: Submission): Promise<SubmitOutcome> => {
    if (opening.kind !== "open") {
      return "failed";
    }

    // Sending deletes the draft, so a save still waiting would fail after it.
    await flushNow();

    const { schema, form, draft } = opening.opened;
    // Each item left unanswered is sent saying so, never left out.
    const toSend = withUnansweredSentAsNotAnswered({
      schema,
      form,
      document: draft.document,
      context: draft.context,
    });

    if (toSend.isErr()) {
      return "failed";
    }

    const request = submissionRequestOf({
      opened: {
        ...opening.opened,
        draft: { ...draft, document: toSend.value },
      },
      assessment,
      assessedOn,
      t,
    });

    if (request === undefined) {
      return "failed";
    }

    const sent = await guestApi.guest.send(request).catch(() => undefined);

    if (sent?.data !== undefined) {
      navigate(guestRoute.sent(), {
        replace: true,
        state: {
          askerName: details.session.sharerName,
        } satisfies SentState,
      });
      return "submitted";
    }

    return sent === undefined ? "failed" : "refused";
  };

  return (
    <GuestAssessmentOverviewContent
      assessment={assessment}
      opening={opening}
      details={details}
      onSend={send}
    />
  );
};

/** What the guest is told beside Send while it is unavailable. */
const notReadyMessage = (
  t: TFunction,
  { commentsMissing, answered }: AssessmentProgress,
): string => {
  if (commentsMissing > 0) {
    return t("guestAssessment.notReady.commentsMissing", {
      count: commentsMissing,
    });
  }

  return answered === 0
    ? t("guestAssessment.notReady.nothingAnswered")
    : t("guestAssessment.notReady.answersToCheck");
};

/** The items sending would leave not answered, by the domain they are in. */
const itemsNotAnsweredIn = ({
  progress: { groups, asked, answered },
  titleOf,
}: {
  progress: AssessmentProgress;
  titleOf: (group: string) => string;
}): ItemsNotAnswered => ({
  count: asked - answered,
  of: asked,
  byDomain: groups.flatMap(({ group, included, items }) => {
    const count = items.filter((item) => !item.answered).length;

    return included && count > 0
      ? [{ group, title: titleOf(group), count }]
      : [];
  }),
});

/**
 * The overview's rendering, with everything it shows injected so a story or
 * a test can show any state of it without an API client.
 */
export const GuestAssessmentOverviewContent = ({
  assessment,
  opening,
  details: { session, message, expiresAt },
  onSend,
}: {
  assessment: Assessment;
  opening: Opening;
  details: GuestAssessmentDetails;
  onSend: (submission: Submission) => Promisable<SubmitOutcome>;
}) => {
  const { t } = useTranslation();
  const [sending, setSending] = useState(false);
  const asker = session.sharerName;

  if (assessment.status !== "open") {
    return <Alert severity="info">{t("guestAssessment.notOpen")}</Alert>;
  }

  if (opening.kind === "loading") {
    return (
      <Typography variant="body2" color="text.secondary">
        {t("common.loading")}
      </Typography>
    );
  }

  if (opening.kind === "failed") {
    return (
      <Alert severity="error">
        {t(`assessment.page.failed.${opening.reason}`)}
      </Alert>
    );
  }

  const { schema, form, draft } = opening.opened;
  const progress = progressOf({
    schema,
    form,
    document: draft.document,
    context: draft.context,
    opened: openedWhereRequired(draft),
    unansweredItemTreatment: "sent_not_answered",
  });

  if (progress.isErr()) {
    return (
      <Alert severity="error">{t("assessment.page.failed.unavailable")}</Alert>
    );
  }

  const titleOf = (group: string) =>
    form.find(
      (element) => element.kind === "group" && element.at.group === group,
    )?.presentation.title.text ?? group;

  return (
    <Stack spacing={3}>
      <Typography variant="h4" component="h1">
        {t("guestAssessment.heading", {
          personFirstName: session.personFirstName,
        })}
      </Typography>
      <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
        <Stack spacing={2} sx={{ flex: 1 }}>
          {message === null ? null : (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" component="h2">
                {t("guestAssessment.messageFrom", { asker })}
              </Typography>
              <Typography
                component="blockquote"
                sx={{ m: 0, mt: 1, whiteSpace: "pre-line" }}
              >
                {message}
              </Typography>
            </Paper>
          )}

          <AssessmentDomainsTable
            progress={progress.value}
            titleOf={titleOf}
            domainPath={(group) => guestRoute.assessmentDomain(group)}
            commentsMissingShown
          />

          <Typography variant="body2">
            {t("guestAssessment.answered", {
              answered: progress.value.answered,
              asked: progress.value.asked,
            })}
          </Typography>

          <Stack direction="row" spacing={2} alignItems="center" useFlexGap>
            <Button
              variant="contained"
              disabled={!progress.value.ready}
              onClick={() => {
                setSending(true);
              }}
            >
              {t("guestAssessment.send", { asker })}
            </Button>
            {progress.value.ready ? null : (
              <Typography variant="body2" color="text.secondary">
                {notReadyMessage(t, progress.value)}
              </Typography>
            )}
          </Stack>
        </Stack>

        <AssessmentFacts
          facts={[
            {
              label: t("guestAssessment.askedBy"),
              value: t("guestAssessment.askerAt", {
                asker,
                organisation: session.organisationName,
              }),
            },
            ...(expiresAt === null
              ? []
              : [
                  {
                    label: t("guestAssessment.sendBy"),
                    value: UK_CALENDAR_DATE.format(expiresAt),
                  },
                ]),
            {
              label: t("assessment.overview.lastActivity"),
              value: CALENDAR_DATE_TIME.format(draft.savedAt),
            },
            {
              label: t("assessment.overview.instrumentVersion"),
              value: assessment.instrument.name,
            },
          ]}
        />
      </Stack>

      {sending ? (
        <SubmitAssessmentDialog
          track={{
            kind: "to_asker",
            askerName: asker,
            notAnswered: itemsNotAnsweredIn({
              progress: progress.value,
              titleOf,
            }),
          }}
          onClose={() => {
            setSending(false);
          }}
          onSubmit={onSend}
        />
      ) : null}
    </Stack>
  );
};
